import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Camera, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CameraOverlay from "./CameraOverlay";

export type PhotoCategory = "hands" | "feet" | "face" | "past_work" | "completion";

interface ClientPhotoUploadProps {
  userId: string;
  category: PhotoCategory;
  bookingId?: string;
  uploadedBy?: "client" | "admin";
  label?: string;
  compact?: boolean;
  onUploadComplete?: () => void;
}

interface PhotoRecord {
  id: string;
  file_path: string;
  created_at: string;
}



const ClientPhotoUpload = ({
  userId,
  category,
  bookingId,
  uploadedBy = "client",
  label,
  compact = false,
  onUploadComplete,
}: ClientPhotoUploadProps) => {
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [mlDetection, setMlDetection] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load ML detection setting
  useEffect(() => {
    const loadSetting = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "camera_ml_detection")
        .maybeSingle();
      if (data) setMlDetection(data.value !== "false");
    };
    loadSetting();
  }, []);

  const fetchPhotos = async () => {
    let query = supabase
      .from("client_photos")
      .select("id, file_path, created_at")
      .eq("user_id", userId)
      .eq("category", category)
      .order("created_at", { ascending: false });

    if (bookingId) {
      query = query.eq("booking_id", bookingId);
    }

    const { data } = await query;
    setPhotos(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchPhotos();
  }, [userId, category, bookingId]);

  const uploadFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const filePath = `${userId}/${category}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("client-photos").upload(filePath, file);
      if (uploadError) throw uploadError;
      const { error: insertError } = await supabase.from("client_photos").insert({
        user_id: userId, category, file_path: filePath,
        booking_id: bookingId || null, uploaded_by: uploadedBy,
      });
      if (insertError) throw insertError;
      toast.success("Photo uploaded!");
      fetchPhotos();
      onUploadComplete?.();
    } catch (err: any) {
      toast.error(err.message || "Upload failed");
    }
    setUploading(false);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleCameraCapture = useCallback(async (file: File) => {
    setCameraOpen(false);
    await uploadFile(file);
  }, [userId, category, bookingId, uploadedBy]);

  const handleDelete = async (photo: PhotoRecord) => {
    try {
      await supabase.storage.from("client-photos").remove([photo.file_path]);
      await supabase.from("client_photos").delete().eq("id", photo.id);
      toast.success("Photo removed");
      fetchPhotos();
    } catch (err: any) {
      toast.error("Failed to delete");
    }
  };

  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    const generateSignedUrls = async () => {
      if (photos.length === 0) return;
      const urls: Record<string, string> = {};
      for (const photo of photos) {
        const { data } = await supabase.storage
          .from("client-photos")
          .createSignedUrl(photo.file_path, 3600);
        if (data?.signedUrl) urls[photo.file_path] = data.signedUrl;
      }
      setSignedUrls(urls);
    };
    generateSignedUrls();
  }, [photos]);

  const getPhotoUrl = (path: string) => signedUrls[path] || "";

  const categoryLabels: Record<PhotoCategory, string> = {
    hands: "Hands",
    feet: "Feet",
    face: "Face",
    past_work: "Past Work",
    completion: "Completed Work",
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleUpload}
          className="hidden"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="rounded-none border-primary/30 hover:bg-primary/10 tracking-[0.15em] uppercase text-xs font-medium gap-2"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          {label || `Upload ${categoryLabels[category]}`}
        </Button>
        {photos.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {photos.length} photo{photos.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs tracking-[0.2em] uppercase font-medium text-muted-foreground">
          {label || categoryLabels[category]}
        </h4>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleUpload}
          className="hidden"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="rounded-none border-primary/30 hover:bg-primary/10 tracking-[0.15em] uppercase text-xs font-medium gap-2"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : photos.length === 0 ? (
        <div
          onClick={() => setCameraOpen(true)}
          className="border border-dashed border-border/50 p-6 text-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
        >
          <Camera size={24} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-xs text-muted-foreground">Tap to open camera</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative group aspect-square">
              <img
                src={getPhotoUrl(photo.file_path)}
                alt={category}
                className="w-full h-full object-cover border border-border/50"
              />
              <button
                onClick={() => handleDelete(photo)}
                className="absolute top-1 right-1 bg-destructive/90 text-destructive-foreground p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {cameraOpen && (
        <CameraOverlay
          category={category}
          onCapture={handleCameraCapture}
          onClose={() => setCameraOpen(false)}
          mlDetection={mlDetection}
        />
      )}
    </div>
  );
};

export default ClientPhotoUpload;

// Helper hook to check if user has required photos
export const useClientPhotoCheck = (userId: string | undefined) => {
  const [photoCounts, setPhotoCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      const { data } = await supabase
        .from("client_photos")
        .select("category")
        .eq("user_id", userId);

      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        counts[row.category] = (counts[row.category] || 0) + 1;
      });
      setPhotoCounts(counts);
      setLoading(false);
    };
    fetch();
  }, [userId]);

  const hasPhoto = (category: string) => (photoCounts[category] || 0) > 0;

  return { photoCounts, hasPhoto, loading };
};
